import {
  Box,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuProps,
  Text,
  Tooltip,
  useFormControl,
} from "@chakra-ui/react";
import { RiArrowDownSLine, RiCloseLine } from "@remixicon/react";
import { forwardRef, useMemo } from "react";

import { MenuArrow } from "@/common/components/MenuArrow";
import MenuDescription from "@/common/components/MenuDescription";
import { PortalWrapper } from "@/common/components/PortalWrapper";
import { StageDisplayName } from "@/stage/components/StageDisplayName";
import { useStagesData } from "@/stage/data/useStagesData";
import { StageDTO } from "@reflag/shared/stageAPI";

export type StagePickerProps = Omit<ButtonProps, "value" | "onChange"> & {
  placement?: MenuProps["placement"];
  size?: ButtonProps["size"];
  placeholder?: string;
  menuDescription?: string | React.ReactNode;
  showArrow?: boolean;
  usePortal?: boolean;
  _group?: ButtonGroupProps;
  onClear?: () => void;
  disabledFilterFn?: (stage: StageDTO) => {
    isDisabled: boolean;
    tooltip?: React.ReactNode;
  };
} & (
    | {
        value?: string | null;
        canClear?: false;
        onChange?: (value: string) => void;
      }
    // Input can be cleared
    | {
        value?: string;
        canClear: true;
        onChange?: (value: string | null) => void;
      }
  );

export const StagePicker = forwardRef(
  (
    {
      id,
      value,
      placement = "bottom-start",
      size = "sm",
      placeholder = "No stage selected",
      menuDescription = "Select stage:",
      showArrow = false,
      usePortal,
      canClear,
      onChange,
      onClear,
      _group,
      disabledFilterFn,
      ...rest
    }: StagePickerProps,
    ref,
  ) => {
    const formControlProps = useFormControl<HTMLButtonElement>(rest);

    const { data: stages = [], isLoading: isLoadingStages } = useStagesData();

    const selectedStage = useMemo(
      () => stages.find((stage) => stage.id === value),
      [stages, value],
    );

    return (
      <Menu autoSelect={false} id={id} placement={placement} closeOnSelect>
        <ButtonGroup isAttached {..._group}>
          <MenuButton
            {...formControlProps}
            ref={ref}
            as={Button}
            color={!value ? "dimmed" : undefined}
            data-testid="stage-picker"
            flexGrow={1}
            isDisabled={formControlProps.disabled}
            isLoading={isLoadingStages}
            loadingText="Loading..."
            mx={-3}
            rightIcon={
              <Box color="dimmed" fontSize="xl" mr={-1}>
                <RiArrowDownSLine size={16} />
              </Box>
            }
            size={size}
            variant="outlineOnHover"
            w="full"
            {...rest}
          >
            {selectedStage ? (
              <StageDisplayName stage={selectedStage} />
            ) : (
              <Text color="dimmed">{placeholder}</Text>
            )}
          </MenuButton>
          {value && canClear && (
            <IconButton
              aria-label="Clear selection"
              icon={<RiCloseLine size={16} />}
              isDisabled={rest.isDisabled}
              marginInlineStart={"-1px"}
              size={size}
              variant="outline"
              onClick={() => {
                onChange?.(null);
                onClear?.();
              }}
            />
          )}
        </ButtonGroup>
        <PortalWrapper usePortal={usePortal}>
          <MenuList
            data-testid="stage-picker-list"
            maxH="sm"
            maxW={280}
            overflow="auto"
            rootProps={
              !usePortal ? { style: { transform: "scale(0)" } } : undefined
            }
          >
            {showArrow && <MenuArrow />}
            <MenuDescription>{menuDescription}</MenuDescription>
            <MenuDivider my={0} />
            <MenuOptionGroup
              type="radio"
              value={value ?? ""}
              onChange={onChange as any}
            >
              {stages.map((stage) => {
                const { isDisabled, tooltip } = disabledFilterFn?.(stage) ?? {};

                const menuItem = (
                  <MenuItemOption
                    key={stage.id}
                    isDisabled={isDisabled}
                    value={stage.id}
                  >
                    <StageDisplayName stage={stage} />
                  </MenuItemOption>
                );

                return tooltip ? (
                  <Tooltip key={stage.id} label={tooltip}>
                    {menuItem}
                  </Tooltip>
                ) : (
                  menuItem
                );
              })}
            </MenuOptionGroup>
          </MenuList>
        </PortalWrapper>
      </Menu>
    );
  },
);

StagePicker.displayName = "StagePicker";
